// createSequence — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'create-sequence' })` op (DSE2 §3.D4).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice D3 — Sequence UI.
//
// Per-engine surface (per DSE2 dialect.sequence):
//   - mssql:  CREATE SEQUENCE [schema.]name [AS <type>] [START WITH n]
//             [INCREMENT BY n] [{MINVALUE n | NO MINVALUE}]
//             [{MAXVALUE n | NO MAXVALUE}] [{CYCLE | NO CYCLE}]
//             [{CACHE n | NO CACHE}]
//   - pgsql:  CREATE SEQUENCE [IF NOT EXISTS] [schema.]name [AS <type>] …
//             [OWNED BY <table.col | NONE>]
//   - mysql:  unsupported — use AUTO_INCREMENT.
//   - oracle: CREATE SEQUENCE [schema.]name … [{ORDER | NOORDER}]
//             [{KEEP | NOKEEP}] [{SCALE [EXTEND] | NOSCALE}]
//             [SHARING = {METADATA | NONE}]. Oracle does not accept AS <type>.
//   - sqlite: unsupported — use INTEGER PRIMARY KEY AUTOINCREMENT.

import type { SqlEngine } from '../../../../sql-grammar/model';
import type { DdlEdit, SequenceDef } from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export interface CreateSequenceArgs {
  readonly sequence: SequenceDef;
}

export function buildCreateSequenceEdit(args: CreateSequenceArgs): DdlEdit {
  return { op: 'create-sequence', sequence: args.sequence };
}

export async function commitCreateSequence(
  ctx: VqeDdlEditContext,
  args: CreateSequenceArgs,
): Promise<void> {
  const edit = buildCreateSequenceEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

const SEQUENCE_SUPPORTED: ReadonlySet<SqlEngine> = new Set([
  'mssql', 'postgres', 'oracle',
] as const);

export function isCreateSequenceSupported(engine: SqlEngine): boolean {
  return SEQUENCE_SUPPORTED.has(engine);
}

export function getCreateSequenceUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'mysql') {
    return (
      'MySQL has no CREATE SEQUENCE. Use an AUTO_INCREMENT column on '
      + 'INTEGER / BIGINT instead.'
    );
  }
  if (engine === 'sqlite') {
    return (
      'SQLite has no CREATE SEQUENCE. Use INTEGER PRIMARY KEY AUTOINCREMENT '
      + 'on the table\'s key column instead.'
    );
  }
  return '';
}

/** Pre-flight validator mirroring DSE2's per-engine reject paths. Returns
 *  `null` when args are acceptable, else a user-facing message. */
export function validateCreateSequenceForEngine(
  engine: SqlEngine,
  args: CreateSequenceArgs,
): string | null {
  if (!isCreateSequenceSupported(engine)) {
    return getCreateSequenceUnsupportedTooltip(engine);
  }
  const seq = args.sequence;
  if (seq.name.name.trim().length === 0) {
    return 'Sequence name is required.';
  }
  if (engine !== 'postgres' && seq.ifNotExists) {
    return `${engine} does not support IF NOT EXISTS on CREATE SEQUENCE (PostgreSQL-only).`;
  }
  if (engine !== 'postgres' && seq.pgsqlOwnedBy) {
    return `${engine} does not support OWNED BY (PostgreSQL-only).`;
  }
  if (engine === 'oracle' && seq.mssqlAs) {
    return 'Oracle does not accept AS <type> on CREATE SEQUENCE.';
  }
  if (engine !== 'oracle') {
    if (seq.oracleOrder !== undefined) {
      return `${engine} does not support ORDER / NOORDER (Oracle-only).`;
    }
    if (seq.oracleKeep !== undefined) {
      return `${engine} does not support KEEP / NOKEEP (Oracle-only).`;
    }
    if (seq.oracleScale !== undefined) {
      return `${engine} does not support SCALE / NOSCALE (Oracle-only).`;
    }
    if (seq.oracleSharing) {
      return `${engine} does not support SHARING = … (Oracle-only).`;
    }
  }
  if (
    seq.minValue !== undefined && seq.minValue !== null
    && seq.maxValue !== undefined && seq.maxValue !== null
    && seq.minValue > seq.maxValue
  ) {
    return 'MINVALUE must be ≤ MAXVALUE.';
  }
  return null;
}
